/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { Directive, ElementRef, HostListener, Input, OnDestroy } from '@angular/core';

@Directive({
    selector: '[appHint]'
})
export class HintDirective implements OnDestroy {
    /**
     * Incoming hint text.
     */
    @Input() public hintText: string = '';

    /**
     * Position of hint in relation to the trigger.
     */
    @Input() public hintPosition: 'top' | 'right' | 'bottom' | 'left' | null = 'top';

    /**
     * Option to not show the hint.
     */
    @Input() public hintShow: boolean = true;

    /**
     * Store the element created on hover.
     * @private
     */
    private hintElement: HTMLDivElement;

    /**
     * Timer id for fading in the hint box.
     * @private
     */
    private timerId: any;

    constructor(
        private elementRef: ElementRef
    ) {
    }

    ngOnDestroy(): void {
        if (this.hintElement) {
            this.hintElement.remove();
        }
    }

    @HostListener('mouseenter') onMouseEnter(): void {
        if (this.hintShow && !this.isDeviceMobile() && this.hintText.length) {
            this.createHintElement();
        }
    }

    @HostListener('mouseleave') onMouseLeave(): void {
        clearTimeout(this.timerId);
        if (this.hintElement) {
            this.hintElement.remove();
        }
    }

    /**
     * Create the hint box
     * @private
     */
    private createHintElement(): void {
        const element: HTMLDivElement = document.createElement('div');
        element.innerHTML = this.hintText;
        element.setAttribute('class', 'hint');
        this.timerId = setTimeout(() => {
            element.classList.add('show');
        }, 450);
        this.setStyle(element, this.getPosition());
        document.body.appendChild(element);
        this.hintElement = element;
    }

    /**
     * Sets styles on an element
     * @param element
     * @param styles
     * @private
     */
    private setStyle(element: HTMLDivElement, styles: any): void {
        Object.keys(styles).forEach((key: string): any => element.style[key] = styles[key]);
    }

    /**
     * Get position co-ordinates for @Input position.
     * @private
     */
    private getPosition(): any {
        const { top, right, bottom, left, height } = this.elementRef.nativeElement.getBoundingClientRect();
        const spacing = 10;

        switch (this.hintPosition) {
            case 'top':
                return {
                    left: (right - left) / 2 + left + 'px',
                    top: (top - 30) + 'px',
                    transform: 'translateX(-50%)'
                };
            case 'right':
                return {
                    left: (right + spacing) + 'px',
                    top: top + (height / 2) + 'px',
                    transform: 'translateY(-50%)'
                };
            case 'bottom':
                return {
                    left: (right - left) / 2 + left + 'px',
                    top: (bottom + spacing) + 'px',
                    transform: 'translateX(-50%)'
                };
            case 'left':
                return {
                    left: (left - spacing) + 'px',
                    top: top + (height / 2) + 'px',
                    transform: 'translate(-100%, -50%)'
                };
        }
    }

    /**
     * Check if the user is using a mobile device.
     * @private
     */
    private isDeviceMobile(): boolean {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    }
}
